import { useAuth } from "@/contexts/AuthContext";
import { submitSurvey } from "@/services/generatedAPIs";
import { SurveyItem, UserInfoResponseRole } from "@/services/generatedAPIs.schemas";
import { useEffect, useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { toast } from "sonner";

type Lang = "zh" | "en";

const content = {
    zh: {
        switchLabel: "English",
        title: "學生版EASE平台使用體驗問卷",
        intro: "感謝閣下使用 EASE 平台！為了進一步提升平台的使用體驗，我們誠摯邀請閣下分享真實的使用體驗。",
        part1Title: "第一部分：平台使用體驗",
        part1Desc: "請根據閣下使用 EASE 平台的真實體驗，針對以下描述選擇最符合的選項（1：非常不同意，2：不同意，3：一般，4：同意，5：非常同意）：",
        likert1: "1 非常不同意",
        likert2: "2 不同意",
        likert3: "3 一般",
        likert4: "4 同意",
        likert5: "5 非常同意",
        q1: "1. EASE 平台容易使用。",
        q1_1: "1.1 在使用平台的過程中，如有不方便使用的地方，請簡單說明：",
        q2: "2. 當我答錯時，系統提供的答題提示對我有幫助。",
        q3: "3. EASE上積分系統的競爭元素令我更加投入。",
        q4: "4. 總體來說，我喜歡這個平台。",
        q4_1: "4.1 請簡單說明你喜歡或不喜歡這個平台的原因：",
        part2Title: "第二部分：學習任務難度與功能輔助",
        q5: "5. EASE平台上的題目難度。",
        q5A: "A. 對我來講太簡單",
        q5B: "B. 對我來講太困難",
        q5C: "C. 整體上適合我",
        q6: "6（多選）EASE平台的以下功能能幫助我的學習：",
        q6a: "a) 做功課",
        q6b: "b) 即時批改",
        q6c: "c) 自動提示",
        q6d: "d) 與 AI 機械人的互動",
        q6e: "e) 完成功課後的對分數的自評",
        q6f: "f) 互動式自我評估",
        q6g: "g) 更多習題",
        q6h: "h) 錯題重練",
        q6i: "i) 自由主題",
        q6j: "j) 記錄我的功課進度及表現",
        q6k: "k) 其他，請說明：",
        q6kPlaceholder: "請說明",
        part3Title: "第三部分：平台使用體驗與意見分享",
        q7: "7. 為了讓 EASE 能更有效地幫助你學習，你對平台有什麼建議？",
        submit: "提交問卷",
    },
    en: {
        switchLabel: "中文",
        title: "EASE Platform User Experience Survey (Student Version)",
        intro: "Thank you for using the EASE platform! To further enhance the user experience on the platform, we sincerely invite you to share your experience.",
        part1Title: "Part 1: Platform User Experience",
        part1Desc: "Based on your experience using the EASE platform, please select the option that best fits the following statements (1: Strongly Disagree, 2: Disagree, 3: Neutral, 4: Agree, 5: Strongly Agree):",
        likert1: "1 Strongly Disagree",
        likert2: "2 Disagree",
        likert3: "3 Neutral",
        likert4: "4 Agree",
        likert5: "5 Strongly Agree",
        q1: "1. The EASE platform is easy to use.",
        q1_1: "1.1 If you encountered any inconveniences while using the platform, please briefly describe them:",
        q2: "2. When I answer a question incorrectly, the hints provided by the system are helpful.",
        q3: "3. The competitive elements of the points system on EASE make me more engaged.",
        q4: "4. Overall, I like this platform.",
        q4_1: "4.1 Please briefly explain why you like or dislike this platform:",
        part2Title: "Part 2: Learning Difficulty and Feature Support",
        q5: "5. The difficulty level of the questions on the EASE platform is:",
        q5A: "A. Too easy for me",
        q5B: "B. Too difficult for me",
        q5C: "C. Generally suitable for me",
        q6: "6. (Multiple Choice) The following features on the EASE platform help my learning:",
        q6a: "a) Completing assignments",
        q6b: "b) Instant marking",
        q6c: "c) Automated hints",
        q6d: "d) Interaction with the AI Chatbot",
        q6e: "e) Self-rating after completing assignments",
        q6f: "f) Interactive self-assessment",
        q6g: "g) Additional practice exercises",
        q6h: "h) Re-practicing incorrect questions",
        q6i: "i) Free-choice topics",
        q6j: "j) Tracking my progress and performance",
        q6k: "k) Other (please specify):",
        q6kPlaceholder: "Please specify",
        part3Title: "Part 3: User Experience & Feedback",
        q7: "7. What suggestions do you have to make EASE a more effective learning tool for you?",
        submit: "Submit Survey",
    },
} as const;

function Survey5({
    t,
    onSuccess,
}: WithTranslation & { onSuccess?: () => void }) {
    const { getCurrentUser } = useAuth();
    const [lang, setLang] = useState<Lang>("zh");
    const c = content[lang];

    const likertQuestions = [
        { id: 1, text: c.q1 },
        { id: 2, text: c.q2 },
        { id: 3, text: c.q3 },
        { id: 4, text: c.q4 },
    ];

    const multiSelectOptions = [
        { value: "a", text: c.q6a },
        { value: "b", text: c.q6b },
        { value: "c", text: c.q6c },
        { value: "d", text: c.q6d },
        { value: "e", text: c.q6e },
        { value: "f", text: c.q6f },
        { value: "g", text: c.q6g },
        { value: "h", text: c.q6h },
        { value: "i", text: c.q6i },
        { value: "j", text: c.q6j },
        { value: "k", text: c.q6k },
    ];

    const q5Options = [
        { value: "A", text: c.q5A },
        { value: "B", text: c.q5B },
        { value: "C", text: c.q5C },
    ];

    const [likertAnswers, setLikertAnswers] = useState<Record<number, string>>({});
    const [q1_1, setQ1_1] = useState("");
    const [q4_1, setQ4_1] = useState("");
    const [q5, setQ5] = useState("");
    const [q6Selected, setQ6Selected] = useState<string[]>([]);
    const [q6Other, setQ6Other] = useState("");
    const [q7, setQ7] = useState("");
    const [answerTimes, setAnswerTimes] = useState<Record<number, string>>({});
    const [startTime, setStartTime] = useState<Date>();

    useEffect(() => {
        setStartTime(new Date());
    }, []);

    const handleQ6Toggle = (value: string) => {
        setQ6Selected(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const items: SurveyItem[] = [];

        for (const q of likertQuestions) {
            const answer = likertAnswers[q.id];
            if (!answer) {
                toast.error(t('component.needanswer'));
                return;
            }
            items.push({ questionId: q.id, answer, answerAt: answerTimes[q.id]! });
        }

        items.push({ questionId: 11, answer: q1_1, answerAt: answerTimes[11] ?? new Date().toISOString() });
        items.push({ questionId: 41, answer: q4_1, answerAt: answerTimes[41] ?? new Date().toISOString() });

        if (!q5) {
            toast.error(t('component.needanswer'));
            return;
        }
        items.push({ questionId: 5, answer: q5, answerAt: answerTimes[5]! });

        if (q6Selected.length === 0) {
            toast.error(t('component.needanswer'));
            return;
        }
        const q6Answer = q6Selected.map(v => v === "k" ? `k) ${q6Other}` : v).join(",");
        items.push({ questionId: 6, answer: q6Answer, answerAt: answerTimes[6]! });

        items.push({ questionId: 7, answer: q7, answerAt: answerTimes[7] ?? new Date().toISOString() });

        submitSurvey({
            userId: getCurrentUser()!.id,
            userType: getCurrentUser()!.role === UserInfoResponseRole.STUDENT ? 1 : 2,
            surveyId: 5,
            startTime: startTime!.toISOString(),
            submitTime: new Date().toISOString(),
            items,
        }).then(rsp => {
            if (rsp.code === 200 && onSuccess) {
                onSuccess();
            }
        }).catch(() => {});

        return false;
    };

    const likertColors = [
        "border-2 border-[#e76f51] peer-checked:bg-[#e76f51] peer-checked:text-white",
        "border-2 border-[#f4a261] peer-checked:bg-[#f4a261] peer-checked:text-white",
        "border-2 border-[#e9c46a] peer-checked:bg-[#e9c46a] peer-checked:text-white",
        "border-2 border-[#95d5b2] peer-checked:bg-[#95d5b2] peer-checked:text-white",
        "border-2 border-[#2a9d8f] peer-checked:bg-[#2a9d8f] peer-checked:text-white",
    ];

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
            <div className="bg-white/80 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-xl w-full max-w-3xl border border-white/50 relative max-h-[90vh] overflow-y-auto">
                {/* Language toggle */}
                <button
                    type="button"
                    className="absolute top-4 right-4 text-sm text-blue-600 hover:text-blue-800 underline"
                    onClick={() => setLang(lang === "zh" ? "en" : "zh")}
                >
                    {c.switchLabel}
                </button>

                <h2 className="text-2xl font-bold text-slate-700 text-center mb-2">{c.title}</h2>
                <p className="text-sm text-slate-600 text-center mb-4">{c.intro}</p>

                <form className="space-y-6" onSubmit={handleSubmit}>
                    {/* Part 1: Platform Experience */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-bold text-slate-700 mb-2">{c.part1Title}</h3>
                        <p className="text-sm text-slate-600 mb-4">{c.part1Desc}</p>

                        <div className="flex gap-4 justify-center mb-4 text-sm text-slate-600">
                            <span>{c.likert1}</span>
                            <span>{c.likert2}</span>
                            <span>{c.likert3}</span>
                            <span>{c.likert4}</span>
                            <span>{c.likert5}</span>
                        </div>

                        {likertQuestions.map((q) => (
                            <div key={q.id} className="mb-4 pb-4 border-b border-slate-100 last:border-b-0">
                                <label className="block font-medium text-m text-black mb-2">
                                    {q.text} <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-6 justify-center">
                                    {[1, 2, 3, 4, 5].map((val, optionIndex) => (
                                        <label key={val} className="flex items-center mr-2">
                                            <input
                                                type="radio"
                                                name={`q${q.id}`}
                                                value={val}
                                                className="peer hidden"
                                                required
                                                onChange={(e) => {
                                                    setLikertAnswers(prev => ({ ...prev, [q.id]: e.target.value }));
                                                    setAnswerTimes(prev => ({ ...prev, [q.id]: new Date().toISOString() }));
                                                }}
                                            />
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center border transition-transform hover:scale-105 ${likertColors[optionIndex]}
                                                peer-checked:[&>svg]:opacity-100 peer-checked:[&>svg]:scale-100`}>
                                                <svg className="w-8 h-8 opacity-0 scale-95 transition-transform" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        </label>
                                    ))}
                                </div>

                                {q.id === 1 && (
                                    <div className="mt-3">
                                        <label className="block text-sm text-slate-600 mb-1">{c.q1_1}</label>
                                        <textarea
                                            className="w-full border rounded-lg p-2 text-sm"
                                            rows={2}
                                            value={q1_1}
                                            onChange={(e) => {
                                                setQ1_1(e.target.value);
                                                setAnswerTimes(prev => ({ ...prev, [11]: new Date().toISOString() }));
                                            }}
                                        />
                                    </div>
                                )}

                                {q.id === 4 && (
                                    <div className="mt-3">
                                        <label className="block text-sm text-slate-600 mb-1">{c.q4_1}</label>
                                        <textarea
                                            className="w-full border rounded-lg p-2 text-sm"
                                            rows={2}
                                            value={q4_1}
                                            onChange={(e) => {
                                                setQ4_1(e.target.value);
                                                setAnswerTimes(prev => ({ ...prev, [41]: new Date().toISOString() }));
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Part 2: Difficulty & Functionality */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-bold text-slate-700 mb-4">{c.part2Title}</h3>

                        <div className="mb-6">
                            <label className="block font-medium text-m text-black mb-2">
                                {c.q5} <span className="text-red-500">*</span>
                            </label>
                            <div className="space-y-2 ml-4">
                                {q5Options.map(opt => (
                                    <label key={opt.value} className="flex items-center">
                                        <input
                                            type="radio"
                                            name="q5"
                                            value={opt.value}
                                            className="mr-2"
                                            required
                                            onChange={(e) => {
                                                setQ5(e.target.value);
                                                setAnswerTimes(prev => ({ ...prev, [5]: new Date().toISOString() }));
                                            }}
                                        />
                                        <span className="text-sm">{opt.text}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block font-medium text-m text-black mb-2">
                                {c.q6} <span className="text-red-500">*</span>
                            </label>
                            <div className="space-y-2 ml-4">
                                {multiSelectOptions.map(opt => (
                                    <label key={opt.value} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            value={opt.value}
                                            className="mr-2"
                                            onChange={() => {
                                            handleQ6Toggle(opt.value);
                                            setAnswerTimes(prev => ({ ...prev, [6]: new Date().toISOString() }));
                                        }}
                                        />
                                        <span className="text-sm">{opt.text}</span>
                                        {opt.value === "k" && (
                                            <input
                                                type="text"
                                                className="ml-2 border rounded px-2 py-1 text-sm flex-1"
                                                placeholder={c.q6kPlaceholder}
                                                value={q6Other}
                                                onChange={(e) => {
                                                    setQ6Other(e.target.value);
                                                    setAnswerTimes(prev => ({ ...prev, [6]: new Date().toISOString() }));
                                                }}
                                            />
                                        )}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Part 3: Suggestions */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="text-lg font-bold text-slate-700 mb-2">{c.part3Title}</h3>
                        <label className="block font-medium text-m text-black mb-2">{c.q7}</label>
                        <textarea
                            className="w-full border rounded-lg p-2 text-sm"
                            rows={3}
                            value={q7}
                            onChange={(e) => {
                                setQ7(e.target.value);
                                setAnswerTimes(prev => ({ ...prev, [7]: new Date().toISOString() }));
                            }}
                        />
                    </div>

                    <div className="text-center mt-8">
                        <button type="submit" className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
                            {c.submit}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default withTranslation()(Survey5);
